import { useState, useContext, useMemo } from 'react'
import ClearIcon from '@mui/icons-material/Clear'
import { CreditCard } from 'lucide-react'
import CustomButton from '../../../../ui/CustomButton'
import { Modal } from '../../../../ui/Modal'
import PaymentForm from '../../../../payment/PaymentForm'
import {
  useMutationUpdateSubscriptionCard,
  useMutationUpdateAddonSubscriptionCard
} from '../../../../../../queries/paymentQueries'
import { ToastContext } from '../../../../../../context/ToastContext'
import { VALIDATION_AMOUNT } from '../../../../../utils/constant'

const PaymentModalShell = ({
  isOpen,
  onClose,
  title,
  description,
  footer,
  widthClass = 'w-[min(48rem,calc(100vw-2rem))]',
  children
}) => (
  <Modal isOpen={isOpen} onClose={onClose}>
    <div
      className={`flex max-h-[80vh] ${widthClass} flex-col overflow-hidden rounded-xl bg-white shadow-2xl`}
    >
      <div className='flex items-center justify-end bg-primary px-4 py-2 text-white sm:px-8'>
        <button
          type='button'
          onClick={onClose}
          className='rounded-full p-2 text-white transition-colors hover:bg-white/15'
          aria-label='Cerrar modal'
        >
          <ClearIcon style={{ fontSize: 20 }} />
        </button>
      </div>

      <div className='flex-1 overflow-y-auto px-6 py-8 sm:px-8'>
        <div className='mb-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-5 shadow-sm'>
          <h2 className='text-2xl font-sans font-bold text-slate-800'>{title}</h2>
          {description && (
            <p className='mt-3 text-sm font-sans leading-6 text-slate-700'>{description}</p>
          )}
        </div>

        {children}
      </div>

      {footer && (
        <div className='flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-8'>
          {footer}
        </div>
      )}
    </div>
  </Modal>
)

const CardDeleteConflictModal = ({
  open,
  onClose,
  card,
  message,
  subscriptions = [],
  addonSubscriptions = [],
  organizationId,
  customerId,
  cardsIds = []
}) => {
  const [updatedSubscriptionIds, setUpdatedSubscriptionIds] = useState(new Set())
  const [updatedAddonIds, setUpdatedAddonIds] = useState(new Set())
  const [activeUpdate, setActiveUpdate] = useState(null)
  const [flowActive, setFlowActive] = useState(false)
  const [flowIndex, setFlowIndex] = useState(0)

  const updateSubscriptionCard = useMutationUpdateSubscriptionCard()
  const updateAddonSubscriptionCard = useMutationUpdateAddonSubscriptionCard()
  const { showToast } = useContext(ToastContext)

  const pendingSubscriptions = subscriptions.filter(subscription => !updatedSubscriptionIds.has(subscription.id))
  const pendingAddons = addonSubscriptions.filter(addon => !updatedAddonIds.has(addon.id))
  const flowPendingItems = useMemo(
    () => [
      ...pendingSubscriptions.map(subscription => ({
        type: 'subscription',
        id: subscription.id,
        name: subscription.name
      })),
      ...pendingAddons.map(addon => ({ type: 'addon', id: addon.id, name: addon.name }))
    ],
    [pendingSubscriptions, pendingAddons]
  )
  const currentFlowItem = flowActive ? flowPendingItems[flowIndex] : null

  const handleOpenSubscriptionForm = subscriptionId => {
    setActiveUpdate({ type: 'subscription', id: subscriptionId })
  }

  const handleOpenAddonForm = addonId => {
    setActiveUpdate({ type: 'addon', id: addonId })
  }

  const handleActiveUpdateSaveCardRequest = async payload => {
    if (!activeUpdate || !organizationId) throw new Error('Faltan datos')

    if (activeUpdate.type === 'subscription') {
      return updateSubscriptionCard.mutateAsync({
        organizationId,
        subscriptionId: activeUpdate.id,
        body: payload
      })
    }

    return updateAddonSubscriptionCard.mutateAsync({
      organizationId,
      addonSubscriptionId: activeUpdate.id,
      body: payload
    })
  }

  const handleFormSuccess = () => {
    if (!activeUpdate) return

    if (activeUpdate.type === 'subscription') {
      setUpdatedSubscriptionIds(prev => new Set([...prev, activeUpdate.id]))
      showToast('success', 'Tarjeta de suscripcion de plan actualizada')
    } else {
      setUpdatedAddonIds(prev => new Set([...prev, activeUpdate.id]))
      showToast('success', 'Tarjeta de add-on actualizada')
    }

    setActiveUpdate(null)
  }

  const handleFlowSaveCardRequest = async payload => {
    if (!currentFlowItem || !organizationId) throw new Error('Faltan datos')

    if (currentFlowItem.type === 'subscription') {
      return updateSubscriptionCard.mutateAsync({
        organizationId,
        subscriptionId: currentFlowItem.id,
        body: payload
      })
    }

    return updateAddonSubscriptionCard.mutateAsync({
      organizationId,
      addonSubscriptionId: currentFlowItem.id,
      body: payload
    })
  }

  const handleFlowSuccess = () => {
    if (!currentFlowItem) return

    if (currentFlowItem.type === 'subscription') {
      setUpdatedSubscriptionIds(prev => new Set([...prev, currentFlowItem.id]))
    } else {
      setUpdatedAddonIds(prev => new Set([...prev, currentFlowItem.id]))
    }

    if (flowIndex + 1 < flowPendingItems.length) {
      setFlowIndex(index => index + 1)
    } else {
      showToast('success', 'Metodos de pago actualizados correctamente')
      setFlowActive(false)
      setFlowIndex(0)
    }
  }

  const handleOpenUpdateFlow = () => {
    if (flowPendingItems.length === 0) return

    setFlowIndex(0)
    setFlowActive(true)
  }

  const handleCloseFlow = () => {
    setFlowActive(false)
    setFlowIndex(0)
  }

  return (
    <>
      <PaymentModalShell
        isOpen={open}
        onClose={onClose}
        title='No se puede eliminar'
        description={message}
        widthClass='w-[min(42rem,calc(100vw-2rem))]'
        footer={
          <>
            <CustomButton
              title='Cerrar'
              color='primary'
              className='rounded-full px-4 py-2'
              onClick={onClose}
            />
            {flowPendingItems.length > 0 && (
              <CustomButton
                title='Actualizar metodo de pago'
                color='accent3'
                className='rounded-full px-4 py-2'
                onClick={handleOpenUpdateFlow}
              />
            )}
          </>
        }
      >
        <div className='space-y-5'>
          <div className='rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'>
            <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.18em] text-slate-500'>
              Tarjeta
            </p>
            <p className='mt-2 text-sm font-sans font-semibold text-slate-800'>
              {card?.cardType?.toUpperCase()} **** {card?.lastFourDigits}
            </p>
          </div>

          <div className='rounded-2xl border border-gray-200 bg-gray-50 p-4'>
            <p className='text-sm font-sans font-medium'>
              Debes actualizar el medio de pago en cada suscripcion antes de eliminar esta
              tarjeta.
            </p>
          </div>

          {subscriptions.length > 0 && (
            <div className='space-y-2'>
              <p className='text-xs font-sans font-semibold uppercase tracking-[0.18em] text-slate-500'>
                Suscripciones de plan
              </p>
              <ul className='space-y-2'>
                {subscriptions.map(subscription => (
                  <li
                    key={subscription.id}
                    className='flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'
                  >
                    <span className='text-sm font-sans text-slate-700'>
                      {subscription.name ?? `Suscripcion #${subscription.id}`}
                    </span>
                    {updatedSubscriptionIds.has(subscription.id) ? (
                      <span className='text-xs font-sans font-medium text-green-600'>Actualizado</span>
                    ) : (
                      <button
                        type='button'
                        onClick={() => handleOpenSubscriptionForm(subscription.id)}
                        className='inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-sans font-medium text-primary transition hover:bg-slate-50'
                      >
                        <CreditCard className='h-4 w-4' />
                        Cambiar tarjeta
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {addonSubscriptions.length > 0 && (
            <div className='space-y-2'>
              <p className='text-xs font-semibold uppercase tracking-[0.18em] text-slate-500'>
                Add-ons
              </p>
              <ul className='space-y-2'>
                {addonSubscriptions.map(addon => (
                  <li
                    key={addon.id}
                    className='flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'
                  >
                    <span className='text-sm text-slate-700'>
                      {addon.name ?? `Addon #${addon.id}`}
                    </span>
                    {updatedAddonIds.has(addon.id) ? (
                      <span className='text-xs font-sans font-medium text-green-600'>Actualizado</span>
                    ) : (
                      <button
                        type='button'
                        onClick={() => handleOpenAddonForm(addon.id)}
                        className='inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-sans font-medium text-primary transition hover:bg-slate-50'
                      >
                        <CreditCard className='h-4 w-4' />
                        Cambiar tarjeta
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </PaymentModalShell>

      {activeUpdate && (
        <PaymentModalShell
          isOpen
          onClose={() => setActiveUpdate(null)}
          title={`Cambiar tarjeta ${activeUpdate.type === 'subscription' ? 'de plan' : 'de add-on'}`}
          description='Registra una nueva tarjeta para actualizar este medio de pago.'
          widthClass='w-[min(40rem,calc(100vw-2rem))]'
          footer={
            <CustomButton
              title='Volver'
              color='primary'
              className='rounded-full px-4 py-2'
              onClick={() => setActiveUpdate(null)}
            />
          }
        >
          <PaymentForm
            amount={VALIDATION_AMOUNT}
            saveCard
            onSaveCardRequest={handleActiveUpdateSaveCardRequest}
            onSuccess={handleFormSuccess}
            brickId={`deleteConflict_${activeUpdate.type}_${activeUpdate.id}`}
            successMessage='Tarjeta actualizada correctamente.'
            managedByParent
          />
        </PaymentModalShell>
      )}

      {flowActive && currentFlowItem && (
        <PaymentModalShell
          isOpen
          onClose={handleCloseFlow}
          title={`Actualizar metodo de pago (${flowIndex + 1}/${flowPendingItems.length})`}
          description={currentFlowItem.name}
          widthClass='w-[min(40rem,calc(100vw-2rem))]'
          footer={
            <CustomButton
              title='Volver'
              color='primary'
              className='rounded-full px-4 py-2'
              onClick={handleCloseFlow}
            />
          }
        >
          <PaymentForm
            amount={VALIDATION_AMOUNT}
            customerId={customerId}
            cardsIds={cardsIds}
            showSavedCard
            saveCard
            onSaveCardRequest={handleFlowSaveCardRequest}
            onSuccess={handleFlowSuccess}
            brickId={`flowUpdate_${flowIndex}_${currentFlowItem.type}_${currentFlowItem.id}`}
            successMessage='Tarjeta actualizada correctamente.'
            managedByParent
          />
        </PaymentModalShell>
      )}
    </>
  )
}

export default CardDeleteConflictModal
